import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import QuickCreateShiftModal, {
  type QuickCreateShiftPrefill,
  type QuickCreateShiftResult,
} from "../components/QuickCreateShiftModal";

export const SHIFT_CREATED_EVENT = "statdoctor:shift-created";

type OpenCreateOptions = {
  onSuccess?: (result?: QuickCreateShiftResult) => void;
};

type CreateShiftContextValue = {
  openCreateShift: (
    prefill?: QuickCreateShiftPrefill | null,
    options?: OpenCreateOptions,
  ) => void;
};

const CreateShiftContext = createContext<CreateShiftContextValue | null>(null);

export function CreateShiftProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [prefill, setPrefill] = useState<QuickCreateShiftPrefill | null>(null);
  const onSuccessRef = useRef<OpenCreateOptions["onSuccess"]>();

  const openCreateShift = useCallback(
    (nextPrefill?: QuickCreateShiftPrefill | null, options?: OpenCreateOptions) => {
      onSuccessRef.current = options?.onSuccess;
      setPrefill(nextPrefill ?? null);
      setOpen(true);
    },
    [],
  );

  const value = useMemo(() => ({ openCreateShift }), [openCreateShift]);

  return (
    <CreateShiftContext.Provider value={value}>
      {children}
      <QuickCreateShiftModal
        open={open}
        prefill={prefill}
        onClose={() => {
          setOpen(false);
          setPrefill(null);
          onSuccessRef.current = undefined;
        }}
        onSuccess={(result) => {
          window.dispatchEvent(new CustomEvent(SHIFT_CREATED_EVENT));
          onSuccessRef.current?.(result);
          setOpen(false);
          setPrefill(null);
          onSuccessRef.current = undefined;
        }}
      />
    </CreateShiftContext.Provider>
  );
}

export function useCreateShift() {
  const context = useContext(CreateShiftContext);
  if (!context) {
    throw new Error("useCreateShift must be used inside CreateShiftProvider");
  }
  return context;
}
